import {
  Box,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowDownToLine,
  faTrashCan,
  faEllipsisVertical,
} from '@fortawesome/pro-regular-svg-icons'
import lightTheme from '../../styles/theme/lightTheme'
import * as React from 'react'
import PropTypes from 'prop-types'
import { downloadFile, FILE_URL } from '../../config'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'

function TabPanel(props) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  )
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
}

const TablePanelBox = styled(TabPanel)(({ theme }) => ({
  height: 'calc(100% - 45px)',
  marginBottom: 10,
  backgroundColor: 'rgb(227, 230, 255, 0.5)',
  '& .MuiBox-root': {
    padding: 0,
  },
}))

const HeaderTab = styled(TableHead)(({ theme }) => ({
  height: 50,
  backgroundColor: lightTheme.palette.primary.b6,
}))

const TitleHead = styled(TableCell)(({ theme }) => ({
  fontFamily: 'Century Gothic Bold',
  fontSize: 10,
  lineHeight: '140%',
  letterSpacing: '0.2px',
  color: lightTheme.palette.text.primary,
  whiteSpace: 'nowrap',

  [theme.breakpoints.up('sm')]: {
    fontSize: 12,
  },
}))

const BodyTable = styled(TableBody)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
}))

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderBottom: '2px solid rgb(227, 230, 255, 0.5)',
  fontFamily: 'Century Gothic',
  fontSize: 10,
  lineHeight: '140%',
  letterSpacing: '0.2px',
  color: lightTheme.palette.text.primary,
  verticalAlign: 'top',

  [theme.breakpoints.up('sm')]: {
    fontSize: 12,
  },
}))

const ActionButton = styled(Box)(({ theme, variant }) => ({
  width: 40,
  height: 40,
  borderRadius: '8px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
  margin: '0 4px',
  transition: 'border-color 0.2s ease, background-color 0.2s ease',
  border:
    variant === 'delete'
      ? `1px solid ${lightTheme.palette.error.dark}`
      : `1px solid ${lightTheme.palette.primary.b2}`,
  '&:hover': {
    borderWidth: 2,
    backgroundColor:
      variant === 'delete'
        ? 'rgba(244, 67, 54, 0.08)'
        : 'rgba(102, 126, 234, 0.12)',
  },
}))

const StyledActionMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 18,
    minWidth: 220,
    border: '1px solid rgba(99, 102, 241, 0.15)',
    boxShadow: '0 24px 60px rgba(99, 102, 241, 0.18)',
  },
}))

const StyledActionMenuItem = styled(MenuItem)(({ theme }) => ({
  px: 2.5,
  py: 1.25,
  borderRadius: 12,
  gap: 10,
  color: lightTheme.palette.text.primary,
  fontFamily: 'Century Gothic',
  fontSize: 14,
  transition: 'background-color 0.2s ease, color 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(102, 126, 234, 0.1)',
  },
  '& .menuActionIcon': {
    minWidth: 24,
    color: '#667eea',
  },
}))

const getFileName = (file) => file.nom?.split('/').pop() || file.nom || '—'

const getFileDate = (file, dateFile) =>
  file.date
    ? format(new Date(file.date), 'dd/MM/yyyy HH:mm')
    : dateFile

const GedTable = ({ files, dateFile, handleRemove }) => {
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = React.useState(null)
  const [menuFile, setMenuFile] = React.useState(null)

  const handleMenuOpen = (event, file) => {
    setAnchorEl(event.currentTarget)
    setMenuFile(file)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setMenuFile(null)
  }

  const handleDownload = (file) => {
    downloadFile(`${FILE_URL}${getFileName(file)}`)
    handleMenuClose()
  }

  const handleDelete = (fileId) => {
    handleRemove(fileId)
    handleMenuClose()
  }

  return (
    <TablePanelBox value={0} index={0}>
      <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <Table
          stickyHeader
          aria-label="documents table"
          sx={{ minWidth: { xs: 260, sm: 'auto' } }}
        >
          <HeaderTab>
            <TableRow>
              <TitleHead sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                {t('profile.date')}
              </TitleHead>
              <TitleHead sx={{ minWidth: { xs: 140, sm: 'auto' } }}>
                {t('profile.documentName')}
              </TitleHead>
              <TitleHead
                sx={{
                  width: { xs: 56, sm: 110 },
                  textAlign: 'center',
                }}
              >
                {t('profile.actions')}
              </TitleHead>
            </TableRow>
          </HeaderTab>
          <BodyTable>
            {files && files.length > 0 ? (
              files.map((file, index) => (
                <TableRow key={file.id ?? `${getFileName(file)}-${index}`}>
                  <StyledTableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    {getFileDate(file, dateFile)}
                  </StyledTableCell>
                  <StyledTableCell sx={{ minWidth: { xs: 140, sm: 'auto' } }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.75,
                      }}
                    >
                      <Typography
                        variant="body2"
                        onClick={() => handleDownload(file)}
                        sx={{
                          fontWeight: 600,
                          color: '#667eea',
                          cursor: 'pointer',
                          lineHeight: 1.35,
                          '&:hover': {
                            color: '#5a67d8',
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        {getFileName(file)}
                      </Typography>
                      <Box
                        sx={{
                          display: { xs: 'flex', md: 'none' },
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 600}}
                        >
                          {t('profile.date')}:
                        </Typography>
                        <Typography variant="caption">
                          {getFileDate(file, dateFile)}
                        </Typography>
                      </Box>
                    </Box>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Box
                      sx={{
                        display: { xs: 'flex', sm: 'none' },
                        justifyContent: 'center',
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, file)}
                        aria-label={t('profile.actions')}
                      >
                        <FontAwesomeIcon
                          icon={faEllipsisVertical}
                          color={lightTheme.palette.primary.b2}
                          fontSize={16}
                        />
                      </IconButton>
                    </Box>

                    <Box
                      sx={{
                        display: { xs: 'none', sm: 'flex' },
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      <Tooltip title={t('box.downloadFiles')} arrow>
                        <ActionButton onClick={() => handleDownload(file)}>
                          <FontAwesomeIcon
                            icon={faArrowDownToLine}
                            color={lightTheme.palette.primary.b2}
                            fontSize={16}
                          />
                        </ActionButton>
                      </Tooltip>
                      {file.id && (
                        <Tooltip title={t('button.delete')} arrow>
                          <ActionButton
                            variant="delete"
                            onClick={() => handleRemove(file.id)}
                          >
                            <FontAwesomeIcon
                              icon={faTrashCan}
                              color={lightTheme.palette.error.dark}
                              fontSize={16}
                            />
                          </ActionButton>
                        </Tooltip>
                      )}
                    </Box>

                    <StyledActionMenu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl) && menuFile === file}
                      onClose={handleMenuClose}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                      PaperProps={{
                        sx: {
                          p: 1,
                          bgcolor: '#fff',
                        },
                      }}
                    >
                      <StyledActionMenuItem onClick={() => handleDownload(file)}>
                        <FontAwesomeIcon
                          icon={faArrowDownToLine}
                          className="menuActionIcon"
                          fontSize={14}
                        />
                        <Typography variant="body2">
                          {t('box.downloadFiles')}
                        </Typography>
                      </StyledActionMenuItem>
                      {file.id && (
                        <StyledActionMenuItem
                          onClick={() => handleDelete(file.id)}
                          sx={{ color: 'error.main' }}
                        >
                          <FontAwesomeIcon
                            icon={faTrashCan}
                            className="menuActionIcon"
                            fontSize={14}
                            style={{ color: 'inherit' }}
                          />
                          <Typography variant="body2" sx={{ color: 'inherit' }}>
                            {t('button.delete')}
                          </Typography>
                        </StyledActionMenuItem>
                      )}
                    </StyledActionMenu>
                  </StyledTableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <StyledTableCell colSpan={3} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2">
                    {t('box.noFile')}
                  </Typography>
                </StyledTableCell>
              </TableRow>
            )}
          </BodyTable>
        </Table>
      </Box>
    </TablePanelBox>
  )
}

export default GedTable
